import React, { useState, useEffect } from 'react';


interface AuthHeroProps {
    variant?: 'candidate' | 'company' | 'recruiter';
    customContent?: {
        title: string;
        description: string;
    };
    showPagination?: boolean;
    autoRotate?: boolean;
}

// Predefined hero content variations
export const HERO_CONTENT = {
    CANDIDATE: [
        {
            title: "Your next opportunity starts here.",
            description: "Create your account, upload your resume, and let Hush's AI instantly find roles tailored to your experience, skills, and ambitions. Discover jobs that fit your life — not just your keywords."
        },
        {
            title: "Smarter matches. Faster hires.",
            description: "Hush uses AI to connect the right people with the right opportunities — no more endless scrolling or guessing. Whether you're hiring or job-hunting, your perfect match might already be waiting for you."
        },
        {
            title: "Recruiting, reinvented with AI.",
            description: "Hush transforms the hiring process from manual and time-consuming to intelligent and effortless. For candidates, it's a faster path to better jobs; for companies, it's a smarter way to grow their teams."
        }
    ],
    COMPANY: {
        title: 'Meet Hush — where talent meets precision.',
        description: 'We believe finding the right job or candidate shouldn\'t feel like a guessing game. Hush\'s AI interprets your goals, preferences, and potential to deliver matches that make sense — not just noise.'
    },
    RECRUITER: {
        title: 'Smarter hiring, human outcomes.',
        description: 'Our AI understands skills, context, and culture fit — so you can focus on people, not paperwork. Hush helps companies find talent with both brains and careers, and candidates find workplaces where they belong.'
    }
} as const;

const AuthHero: React.FC<AuthHeroProps> = ({
    variant = 'candidate',
    customContent,
    showPagination = true,
    autoRotate = true
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);

    // Determine content based on variant or custom content
    const getContent = () => {
        if (customContent) {
            return customContent;
        }

        if (variant === 'candidate') {
            return HERO_CONTENT.CANDIDATE[currentSlide];
        } else if (variant === 'company') {
            return HERO_CONTENT.COMPANY;
        } else {
            return HERO_CONTENT.RECRUITER;
        }
    };

    const content = getContent();

    useEffect(() => {
        if (autoRotate && variant === 'candidate' && !customContent) {
            const interval = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % HERO_CONTENT.CANDIDATE.length);
            }, 5000);

            return () => clearInterval(interval);
        }
    }, [autoRotate, variant, customContent]);


    // Unified design for all variants - only text content differs
    return (
        <div className="relative flex-1 bg-[#015FA6] overflow-hidden min-h-screen">
            {/* Background Rectangle SVG */}
            <img
                src="/authpage/Reactangle.svg"
                alt=""
                className="absolute w-full h-full object-cover top-0 left-0"
            />

            {/* Gradient Ellipse SVG */}
            <img
                src="/authpage/ellipse.svg"
                alt=""
                className="absolute w-[60%] max-w-[800px] h-auto -top-1/3 left-1/4 opacity-80"
            />

            {/* Hero Text */}
            <div className="absolute inset-0 flex items-center justify-center px-8 sm:px-12 lg:px-16 z-10">
                <div className="max-w-[821px] text-center">
                    <h2 className="font-manrope font-semibold text-3xl sm:text-4xl lg:text-[40px] leading-tight text-[#F8F9FB] mb-6 sm:mb-8">
                        {content.title}
                    </h2>
                    <p className="font-manrope text-base sm:text-lg lg:text-xl leading-relaxed text-[#F8F9FB] max-w-[653px] mx-auto">
                        {content.description}
                    </p>
                </div>
            </div>

            {/* Pagination Dots - Only show for candidate variant */}
            {showPagination && variant === 'candidate' && (
                <div className="absolute bottom-8 sm:bottom-12 lg:bottom-[204px] left-1/2 -translate-x-1/2 flex gap-3 items-center z-10">
                    {[0, 1, 2].map((index) => (
                        <div
                            key={index}
                            className={`
                                transition-all duration-300
                                ${index === currentSlide
                                    ? 'w-6 h-6 border-2 border-white rounded-full flex items-center justify-center'
                                    : 'w-2 h-2 bg-white/30 border border-white rounded-full'
                                }
                            `}
                        >
                            {index === currentSlide && (
                                <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AuthHero;
